import get from 'lodash/get';

const PATHS = {
    revision: (revisionId) => `/revisions/${revisionId}`,
    revisionData: (revisionId) => `/revisions/${revisionId}/data`,
    test: (testId) => `/tests/${testId}`,
    testResults: (testId) => `/tests/${testId}/revisions`,
};

const ERROR_TO_USER_MESSAGE = {
    401: 'Invalid session, please log back in.',
    invalid_grant: 'Your authorization request was not valid.',
    'Network Error': 'Unable to connect to cloud service provider. Please try again.',
};

class S3Service {
    /**
     * Should be implemented in a wrapper
     */
    static getAccessToken;
    static getRefreshToken;
    static refreshTokenCallback;

    constructor(restService) {
        this.restService = restService;
    }

    async downloadFiles(revisionId) {
        const revision = await this.#getRevision(revisionId);
        const testId = get(revision, 'data.relationships.test.data.id');
        const testData = await this.#getTest(testId);
        const trackingNumber = get(testData, 'data.attributes.tracking_number');

        const urls = [];
        const fileNames = [
            `test${testId}-revision${revisionId}.edf`,
            `test${testId}-revision${revisionId}.annotation`,
        ];
        const { edf, annotations } = await this.#getRevisionData(revisionId);

        edf && urls.push(edf);
        annotations && urls.push(annotations);

        const promises = urls.map((url, idx) => this.#downloadBlobFile(url, fileNames[idx]));
        const data = await Promise.all(promises);

        return {
            data,
            meta: {
                testId,
                revisionId,
                trackingNumber,
            },
        };
    }

    async submitTestResults(testId, parentRevisionId, comments, testResults) {
        return this.restService
            .post(
                PATHS.testResults(testId),
                {
                    author_comment: comments,
                    test_results: testResults,
                    parent_id: parentRevisionId,
                },
                {
                    token: S3Service.getAccessToken(),
                }
            )
            .catch(this.#handleErrors);
    }

    async getRevisionSignedURLS(revisionId) {
        return this.restService
            .post(PATHS.revisionData(revisionId), {}, { token: S3Service.getAccessToken() })
            .catch(this.#handleErrors);
    }

    async uploadBlob(url, blob) {
        return this.restService.put(url, blob).catch(this.#handleErrors);
    }

    async #downloadBlobFile(url, fileName) {
        try {
            const { data } = await this.restService.get(url, { responseType: 'blob' });

            return Promise.resolve(new File([data], fileName));
        } catch (error) {
            return this.#handleErrors(error);
        }
    }

    async #getRevision(revisionId) {
        try {
            const { data } = await this.restService.get(PATHS.revision(revisionId), {
                token: S3Service.getAccessToken(),
            });

            return Promise.resolve(data);
        } catch (error) {
            return this.#handleErrors(error);
        }
    }

    async #getRevisionData(revisionId) {
        try {
            const { data } = await this.restService.get(PATHS.revisionData(revisionId), {
                token: S3Service.getAccessToken(),
            });

            return Promise.resolve(data);
        } catch (error) {
            return this.#handleErrors(error);
        }
    }

    async #getTest(testId) {
        try {
            const { data } = await this.restService.get(PATHS.test(testId), {
                token: S3Service.getAccessToken(),
            });

            return Promise.resolve(data);
        } catch (error) {
            return this.#handleErrors(error);
        }
    }

    #handleErrors(error) {
        let errorMessages = [];
        const friendlyMessage = ERROR_TO_USER_MESSAGE[get(error, 'response.data.error')];
        const serverErrorMessages = get(error, 'response.data.errors');
        const axiosFriendlyMessage = ERROR_TO_USER_MESSAGE[get(error, 'message')];

        if (friendlyMessage) {
            errorMessages.push(friendlyMessage);
        } else if (serverErrorMessages) {
            errorMessages.concat(serverErrorMessages);
        } else if (axiosFriendlyMessage) {
            errorMessages.push(axiosFriendlyMessage);
        }

        return Promise.reject(errorMessages);
    }
}

export default S3Service;
